/**
 * NoteEditCell - Inline note editing with save on blur/Enter.
 *
 * Saves only when the user finishes editing (blur or Enter key),
 * preventing partial saves during typing.
 */
import { useState, useEffect, useRef } from 'react';
import { TextInput } from '@mantine/core';

interface NoteEditCellProps {
  value: string;
  onSave: (newNote: string) => void;
  disabled?: boolean;
}

export function NoteEditCell({ value, onSave, disabled = false }: NoteEditCellProps) {
  const [localValue, setLocalValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const savedValueRef = useRef(value);
  const skipBlurSaveRef = useRef(false);

  // Track the last saved/prop value
  useEffect(() => {
    savedValueRef.current = value;
  }, [value]);

  // Update local value when prop changes, but only if not currently focused
  // (prevents overwriting user input mid-edit from a refetch)
  useEffect(() => {
    if (!isFocused) {
      setLocalValue(value);
    }
  }, [value, isFocused]);

  const saveIfChanged = () => {
    const trimmed = localValue.trim();
    // Only save if the value actually changed from what the server has
    if (trimmed !== savedValueRef.current) {
      onSave(trimmed);
      // Immediately mark as saved to prevent double-fire
      // (Enter calls saveIfChanged, then blur() triggers it again)
      savedValueRef.current = trimmed;
    }
    // Also update local display to the trimmed version
    setLocalValue(trimmed);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(event.target.value);
  };

  const handleBlur = () => {
    setIsFocused(false);
    // Skip save when Escape was pressed (blur was triggered programmatically
    // after reverting localValue, but React hasn't re-rendered yet so
    // saveIfChanged would read the stale pre-revert value)
    if (skipBlurSaveRef.current) {
      skipBlurSaveRef.current = false;
      return;
    }
    saveIfChanged();
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      saveIfChanged();
      // Blur the input after Enter to indicate the save happened
      (event.target as HTMLInputElement).blur();
    }
    if (event.key === 'Escape') {
      // Revert to the last saved value on Escape
      setLocalValue(savedValueRef.current);
      // Skip the blur-triggered save — localValue hasn't re-rendered yet
      // so saveIfChanged would read the stale pre-revert value
      skipBlurSaveRef.current = true;
      (event.target as HTMLInputElement).blur();
    }
  };

  return (
    <TextInput
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      placeholder="Add note..."
      size="sm"
      disabled={disabled}
      styles={{
        input: {
          fontSize: '14px',
          padding: '4px 8px',
        },
      }}
    />
  );
}
